import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { filter, take } from 'rxjs';
import { ScrapCategoryService, ScrapCategoryDto, CreateScrapCategoryDto } from '../../services/scrap-category.service';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-scrap-categories',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './scrap-categories.component.html',
  styleUrl: './scrap-categories.component.css'
})
export class ScrapCategoriesComponent implements OnInit {
  categories: ScrapCategoryDto[] = [];
  filteredCategories: ScrapCategoryDto[] = [];

  readonly cdnUrl = environment.cdnUrl;

  isLoading = false;
  showCreateForm = false;
  isSubmitting = false;

  searchQuery = '';

  categoryData: CreateScrapCategoryDto = { name: '', image: '', isScrap: true };
  formErrors: { [key: string]: string } = {};

  notification = {
    show: false,
    message: '',
    type: 'success' as 'success' | 'error' | 'info'
  };

  constructor(
    private scrapCategoryService: ScrapCategoryService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.authService.getMsalInitializedObservable()
      .pipe(
        filter(initialized => initialized),
        take(1)
      )
      .subscribe(() => this.loadCategories());
  }

  loadCategories(): void {
    this.isLoading = true;
    this.scrapCategoryService.getScrapCategories().subscribe({
      next: (categories) => {
        this.categories = categories;
        this.applyFilter();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading scrap categories:', error);
        this.showNotification('Failed to load scrap categories. Please try again.', 'error');
        this.isLoading = false;
      }
    });
  }

  applyFilter(): void {
    const query = this.searchQuery.trim().toLowerCase();
    this.filteredCategories = query
      ? this.categories.filter(c => c.name.toLowerCase().includes(query))
      : [...this.categories];
  }

  onSearchChange(): void {
    this.applyFilter();
  }

  toggleCreateForm(): void {
    this.showCreateForm = !this.showCreateForm;
    if (!this.showCreateForm) {
      this.resetForm();
    }
  }

  validateForm(): boolean {
    this.formErrors = {};
    if (!this.categoryData.name?.trim()) {
      this.formErrors['name'] = 'Category name is required';
    } else if (this.categoryData.name.trim().length < 2) {
      this.formErrors['name'] = 'Must be at least 2 characters';
    } else if (this.categoryData.name.trim().length > 100) {
      this.formErrors['name'] = 'Must be less than 100 characters';
    }
    return Object.keys(this.formErrors).length === 0;
  }

  onSubmit(): void {
    if (!this.validateForm()) return;

    this.isSubmitting = true;
    const payload: CreateScrapCategoryDto = {
      name: this.categoryData.name.trim(),
      image: this.categoryData.image?.trim() || undefined,
      isScrap: this.categoryData.isScrap
    };

    this.scrapCategoryService.createScrapCategory(payload).subscribe({
      next: () => {
        this.showNotification('Scrap category created successfully!', 'success');
        this.resetForm();
        this.showCreateForm = false;
        this.loadCategories();
      },
      error: (error) => {
        console.error('Error creating scrap category:', error);
        this.handleServerErrors(error);
        this.isSubmitting = false;
      }
    });
  }

  private handleServerErrors(error: any): void {
    this.formErrors = {};
    if (error?.error?.errors) {
      Object.keys(error.error.errors).forEach(key => {
        const fieldName = key.toLowerCase();
        this.formErrors[fieldName] = Array.isArray(error.error.errors[key])
          ? error.error.errors[key][0]
          : error.error.errors[key];
      });
    } else if (error?.error?.message) {
      this.formErrors['general'] = error.error.message;
    } else {
      this.formErrors['general'] = 'An unexpected error occurred. Please try again.';
    }
  }

  onCancelCreate(): void {
    this.showCreateForm = false;
    this.resetForm();
  }

  private resetForm(): void {
    this.categoryData = { name: '', image: '', isScrap: true };
    this.formErrors = {};
    this.isSubmitting = false;
  }

  hasError(field: string): boolean {
    return !!this.formErrors[field];
  }

  getError(field: string): string {
    return this.formErrors[field] || '';
  }

  showNotification(message: string, type: 'success' | 'error' | 'info' = 'success'): void {
    this.notification = { show: true, message, type };
    setTimeout(() => {
      if (this.notification.message === message) {
        this.notification.show = false;
      }
    }, 3000);
  }

  closeNotification(): void {
    this.notification.show = false;
  }
}
